import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { ClienteService } from '../../services/cliente/cliente.service';
import { DialogService } from '../../shared/services/dialog.service';
import { Cliente } from '../../domain/cliente.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-clientes',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,  // TableModule incluye p-columnFilter
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.scss']
})
export class ClientesComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  // Signals para estado reactivo
  clientes = signal<Cliente[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Cliente[] = [];
  filterValue: string = '';  // NO usar signal para filtro global (compatibilidad con PrimeNG)

  // Configuración
  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  // Control de suscripciones y reintentos
  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000; // 1 minuto

  // Columnas de la tabla
  // Nota: Los campos deben coincidir con los nombres del backend para filtros
  columnsTable: Column[] = [
    { field: 'razonSocial', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombreFantasia', header: 'Apellido', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'tipoCliente', header: 'Tipo', fieldType: 'text', sortable: false, exportable: false, filtrable: false },
    { field: 'email', header: 'Email', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'telefono', header: 'Teléfono', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'agenciaProductor', header: 'Agencia/Productor', fieldType: 'boolean-icon', sortable: false, exportable: false, filtrable: false },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  // Para filtros y ordenamiento
  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  // Helper para limpiar tabla
  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private clienteService: ClienteService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    // Manejo del bug de bucle infinito al hacer clear
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      // API Platform usa páginas basadas en 1, PrimeNG usa índice basado en 0
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';  // Para texto
  }

  cargar(): void {
    // Verificar si hemos excedido el límite de errores
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.clienteService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          // Mantener campos del backend para filtros y ordenamiento
          const clientesMapeados = members.map((cliente: any) => ({
            id: cliente.id,
            uuid: cliente.uuid,
            razonSocial: cliente.razonSocial,
            nombreFantasia: cliente.nombreFantasia,
            nombre: cliente.nombre,
            apellido: cliente.apellido,
            email: cliente.email,
            telefono: cliente.telefono,
            direccion: cliente.domicilio,
            tipoCliente: cliente.tipoCliente || [],
            agenciaProductor: cliente.agenciaProductor ? {
              id: cliente.agenciaProductor.id,
              razonSocial: cliente.agenciaProductor.razonSocial,
              nombreFantasia: cliente.agenciaProductor.nombreFantasia,
              nombre: cliente.agenciaProductor.nombre,
              apellido: cliente.agenciaProductor.apellido,
              email: cliente.agenciaProductor.email,
              telefono: cliente.agenciaProductor.telefono,
              tipoCliente: cliente.agenciaProductor.tipoCliente
            } : null,
            active: cliente.active ?? true,
            selected: false
          }));

          this.clientes.set(clientesMapeados);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || clientesMapeados.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar clientes (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar clientes (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.clientes.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleClienteEnabled(cliente: Cliente): void {
    if (!cliente.id) return;

    const nuevoEstado = cliente.active ?? true;

    const clienteActualizado: Cliente = {
      active: nuevoEstado
    };

    this.loading.set(true);
    this.clienteService.patch(cliente.id, clienteActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: Cliente) => {
          this.loading.set(false);
          const estadoFinal = resp.active ?? false;
          const accionTxt = estadoFinal ? 'activado' : 'desactivado';
          this.messageService.add({
            severity: 'success',
            summary: `Cliente ${accionTxt}`,
            detail: `El cliente ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          // Revertir el cambio en caso de error
          cliente.active = !nuevoEstado;
          console.error('Error al actualizar cliente:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado del cliente',
            life: 3000
          });
        }
      });
  }

  editarCliente(id: string): void {
    this.router.navigate(['/clientes/edit', id]);
  }

  navigateToCreateCliente(): void {
    this.router.navigate(['/clientes/edit/nuevo']);
  }

  eliminarCliente(cliente: Cliente): void {
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactivar al cliente ${cliente.razonSocial || cliente.nombre || ''} ${cliente.nombreFantasia || cliente.apellido || ''}?`,
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!cliente.id) return;

        this.loading.set(true);
        const clienteActualizado: Cliente = {
          active: false
        };

        this.clienteService.patch(cliente.id, clienteActualizado)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Cliente Desactivado',
                detail: 'El cliente ha sido desactivado correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error al desactivar cliente:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactivar el cliente',
                life: 3000
              });
            }
          });
      }
    });
  }

  activarCliente(cliente: Cliente): void {
    if (!cliente.id) return;

    this.loading.set(true);
    const clienteActualizado: Cliente = {
      active: true
    };

    this.clienteService.patch(cliente.id, clienteActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Cliente Activado',
            detail: 'El cliente ha sido activado correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al activar cliente:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activar el cliente',
            life: 3000
          });
        }
      });
  }

  getAgenciaProductorDisplay(agenciaProductor: Cliente | string | null | undefined): string {
    if (!agenciaProductor) {
      return '-';
    }

    if (typeof agenciaProductor === 'string') {
      return agenciaProductor;
    }

    // Si es un objeto Cliente, mostrar razón social o nombre
    const razonSocial = agenciaProductor.razonSocial;
    const nombreFantasia = agenciaProductor.nombreFantasia;
    const nombre = agenciaProductor.nombre;
    const apellido = agenciaProductor.apellido;

    if (razonSocial) {
      return nombreFantasia ? `${razonSocial} (${nombreFantasia})` : razonSocial;
    }

    if (nombre && apellido) {
      return `${nombre} ${apellido}`;
    }

    if (nombre) {
      return nombre;
    }

    return '-';
  }

  getAgenciaProductorEmail(agenciaProductor: Cliente | string | null | undefined): string | null {
    if (!agenciaProductor || typeof agenciaProductor === 'string') {
      return null;
    }

    return agenciaProductor.email || null;
  }

  getAgenciaProductorTelefono(agenciaProductor: Cliente | string | null | undefined): string | null {
    if (!agenciaProductor || typeof agenciaProductor === 'string') {
      return null;
    }

    return agenciaProductor.telefono || null;
  }

  /**
   * Obtiene los tipos de cliente de un cliente
   */
  getTiposCliente(cliente: Cliente): ('anunciante' | 'agencia' | 'productor')[] {
    return cliente.tipoCliente || [];
  }

  /**
   * Verifica si un cliente tiene un tipo específico
   */
  tieneTipo(cliente: Cliente, tipo: 'anunciante' | 'agencia' | 'productor'): boolean {
    const tipos = this.getTiposCliente(cliente);
    return tipos.includes(tipo);
  }

  /**
   * Obtiene el label y estilo para un tipo de cliente
   */
  getTipoClienteInfo(tipo: 'anunciante' | 'agencia' | 'productor'): { label: string; class: string; icon: string } {
    const tiposInfo: Record<'anunciante' | 'agencia' | 'productor', { label: string; class: string; icon: string }> = {
      anunciante: {
        label: 'Anunciante',
        class: 'app-badge-primary',
        icon: 'pi pi-megaphone'
      },
      agencia: {
        label: 'Agencia',
        class: 'app-badge-success',
        icon: 'pi pi-building'
      },
      productor: {
        label: 'Productor',
        class: 'app-badge-warning',
        icon: 'pi pi-briefcase'
      }
    };
    return tiposInfo[tipo];
  }
}
